// The calendar: every dated commitment, on the day it lands.
//
// The other screens are each about one table — deals, bills, partners — and the
// question this one answers falls between all of them: what is coming up. A
// bill going out on the 1st, a partner's split stepping down on the 15th and an
// implementation fee that cannot be priced on a deal starting the 20th are one
// month's work, and until now they lived on three screens that never appeared
// together.
//
// Everything on it comes from ONE request (`/api/reports/calendar`), for the
// three reasons the contract's `CalendarReport` states at length: the list
// endpoints truncate silently, two of the seven kinds are resolutions rather
// than rows, and a report may convert currencies because it echoes the dated
// rate it used. That last one is why this screen shows a single figure per day
// where the billing calendar has to show one per currency — the footnote under
// the grid is the rate, and it is what makes a converted total honest.
//
// TWO VIEWS, ONE PAGE. The grid answers "how is the month shaped"; the agenda
// answers "what is next", skipping the empty days. Same reasoning as the
// billing screen's two views: the month, the kind filter and the partner scope
// are shared, and a second route would duplicate them and then drift.
//
// WHAT IT DOES NOT DO: it writes nothing. Every event is a date the data
// already holds, and every chip is a way IN to the record that owns it — the
// deal, or the partner. Nothing here is a reminder or an assignment.

import { useCallback, useMemo, useState, type ReactNode } from "react";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { CalendarEventKind, Currency, type CalendarEvent, type ReportScope } from "@crm/shared";
import styles from "./CalendarPage.module.css";
import {
  buildAgenda,
  buildEventCalendar,
  type CalendarDay,
  type CalendarMonthModel,
} from "./calendarModel.js";
import {
  calendarEventKindTone,
  calendarEventLabel,
  calendarEventTarget,
  describeCalendarEvent,
} from "./calendarEvents.js";
import { calendarCsv } from "./calendarCsv.js";
import {
  calendarPageToQuery,
  calendarPageToSearch,
  currentMonth,
  searchToCalendarPage,
  shiftMonth,
  toggleKind,
  type CalendarPageState,
  type CalendarSearch,
  type CalendarViewMode,
} from "./calendarSearch.js";
import { Badge } from "../../design/Badge.js";
import { Button } from "../../design/Button.js";
import { Card, CardHeader } from "../../design/Card.js";
import { EmptyState } from "../../design/Feedback.js";
import { FxDisplayFootnote } from "../../design/FxFootnote.js";
import { QueryBoundary } from "../../design/QueryBoundary.js";
import { Segmented, type SegmentedOption } from "../../design/Segmented.js";
import { StatTile } from "../../design/StatTile.js";
import { usePageChrome } from "../../design/pageChrome.js";
import { usePartnerOptions, useCalendarReport } from "../../api/queries.js";
import {
  addMonths,
  formatCalendarDate,
  formatCalendarMonth,
  todayIsoDate,
  weekdayHeadings,
} from "../../format/calendarDate.js";
import { calendarEventKindLabel } from "../../format/labels.js";
import { formatCount, formatCurrencyTotals, formatMoney } from "../../format/money.js";
import { useReportCurrency } from "../../format/reportCurrency.js";
import { useDebouncedValue } from "../../lib/useDebouncedValue.js";

/**
 * How many events a day cell shows before it collapses the rest behind a count.
 * Three keeps every row of the grid the same height in an ordinary month;
 * without a cap, one month-end day with eleven events sets the height of the
 * whole week. The rest are one click away in the day panel, which is a better
 * place to read eleven things than a cell 100px tall.
 */
const EVENTS_PER_DAY = 3;

/** One identity for "nothing loaded yet", so the model memo does not rebuild
 *  the whole grid on every render before the query settles. */
const NO_EVENTS: readonly CalendarEvent[] = [];

const VIEW_OPTIONS: ReadonlyArray<SegmentedOption<CalendarViewMode>> = [
  { value: "month", label: "Month", title: "The month, by the day each thing lands" },
  { value: "agenda", label: "Agenda", title: "The same events as a list, empty days skipped" },
];

export function CalendarPage(): ReactNode {
  const search = useSearch({ from: "/calendar" });
  const navigate = useNavigate();
  // Read once per render and threaded through, so the grid, the "Today" control
  // and the URL's default month cannot disagree about what day it is.
  const today = todayIsoDate();
  const state = useMemo(() => searchToCalendarPage(search, today), [search, today]);

  const setState = useCallback(
    (next: CalendarPageState) => {
      void navigate({
        to: "/calendar",
        search: calendarPageToSearch(next, today) as CalendarSearch,
      });
    },
    [navigate, today],
  );

  const reportCurrency = useReportCurrency();
  const query = useMemo(() => calendarPageToQuery(state, reportCurrency), [state, reportCurrency]);
  // The chips and the month arrows move the URL at once; the report follows
  // once they hold still, so a run of clicks asks the server once.
  const report = useCalendarReport(useDebouncedValue(query));
  const events = report.data?.events ?? NO_EVENTS;

  const model = useMemo(
    () => buildEventCalendar(state.month, events, today),
    [state.month, events, today],
  );
  const agenda = useMemo(() => buildAgenda(events, today), [events, today]);

  const monthLabel = formatCalendarMonth(state.month);
  const currency = report.data?.currency ?? reportCurrency;

  // A COUNT sums across currencies where a money figure may not: it is a
  // number of bills, not an amount.
  const unresolvedBillCount = (report.data?.billing_totals ?? []).reduce(
    (total, entry) => total + entry.unresolved_item_count,
    0,
  );

  // Which day the panel is showing. Null until somebody picks one — the grid is
  // the primary reading and the panel is the follow-up question.
  const [selected, setSelected] = useState<string | null>(null);
  const selectedDay = useMemo(
    () =>
      selected === null
        ? null
        : (model.weeks.flatMap((week) => week.days).find((day) => day.iso === selected) ?? null),
    [model, selected],
  );

  const exportCsv = useCallback(() => {
    downloadCsv(events, currency, monthLabel);
  }, [events, currency, monthLabel]);

  usePageChrome({
    title: "Calendar",
    ...(events.length === 0
      ? {}
      : { onExport: exportCsv, exportLabel: `Export everything shown for ${monthLabel}` }),
  });

  return (
    <div className={styles.page}>
      <div className={styles.toolbar}>
        <Segmented
          label="Calendar view"
          variant="inset"
          options={VIEW_OPTIONS}
          value={state.view}
          onChange={(view) => setState({ ...state, view })}
        />

        <PartnerFilter state={state} setState={setState} />

        <div className={styles.monthNav}>
          <Button
            small
            aria-label={`Previous month, ${formatCalendarMonth(addMonths(state.month, -1))}`}
            onClick={() => setState(shiftMonth(state, -1))}
          >
            ‹
          </Button>
          <h2 className={styles.monthTitle}>{monthLabel}</h2>
          <Button
            small
            aria-label={`Next month, ${formatCalendarMonth(addMonths(state.month, 1))}`}
            onClick={() => setState(shiftMonth(state, 1))}
          >
            ›
          </Button>
          <Button small onClick={() => setState({ ...state, month: currentMonth(today) })}>
            Today
          </Button>
        </div>
      </div>

      <KindFilter state={state} setState={setState} counts={report.data?.kind_counts} />

      <div className={styles.stats}>
        <StatTile
          label={`Landing in ${monthLabel}`}
          value={report.data === undefined ? null : formatCount(model.monthEventCount)}
          hint="Every kind currently shown, on the days that belong to this month."
        />
        <StatTile
          label="Bills to send"
          value={
            report.data === undefined
              ? null
              : formatMoney(model.monthBillGrossMinor, currency, { omitCurrencyCode: true })
          }
          hint={`${formatCount(model.monthBillCount)} across the month, gross. Cost recharges are counted separately.`}
        />
        <StatTile
          label="Our share of those"
          value={
            report.data === undefined
              ? null
              : formatCurrencyTotals(
                  report.data.billing_totals.map((total) => ({
                    currency: total.currency,
                    minor: total.our_share_minor,
                  })),
                )
          }
          // A bill's split is derived from its agreement until the day it goes
          // out (migration 0013), and a bill whose agreement covers no term on
          // its due date has NO share. Those are in the gross tile beside this
          // one and in none of this one, so the count has to be here or the
          // figure quietly under-reports the window.
          hint={
            unresolvedBillCount === 0
              ? "Across the whole visible window, at each bill's own split."
              : `Across the whole visible window. ${formatCount(unresolvedBillCount)} bill${unresolvedBillCount === 1 ? "" : "s"} cannot resolve a split and are not in this figure — they are in the gross beside it.`
          }
        />
        <StatTile
          label="Cost recharges"
          value={
            report.data === undefined
              ? null
              : formatCurrencyTotals(
                  report.data.cost_totals.map((total) => ({
                    currency: total.currency,
                    minor: total.partner_share_minor,
                  })),
                )
          }
          hint="What goes on the partners' bills — never added to the gross above."
        />
      </div>

      <Card>
        <QueryBoundary query={report} label="the calendar">
          {(loaded) => (
            <>
              {loaded.events.length === 0 && (
                <EmptyState
                  title={`Nothing lands in ${monthLabel}`}
                  description="No bill, term change, implementation fee, price change or deal milestone falls in this window under the current filters. Widen the kinds, clear the partner filter, or page to another month."
                />
              )}
              {state.view === "month" ? (
                <MonthGrid
                  model={model}
                  monthLabel={monthLabel}
                  currency={currency}
                  selected={selected}
                  onSelect={setSelected}
                />
              ) : (
                <Agenda days={agenda} currency={currency} />
              )}
            </>
          )}
        </QueryBoundary>
      </Card>

      {state.view === "month" && selectedDay !== null && (
        <DayPanel day={selectedDay} currency={currency} onClose={() => setSelected(null)} />
      )}

      {report.data !== undefined && (
        <FxDisplayFootnote fx={report.data.fx} generatedAt={report.data.generated_at} />
      )}
    </div>
  );
}

/**
 * The kind chips, which are also the legend.
 *
 * One control doing both jobs on purpose: the colour a chip carries in the grid
 * is the colour of the chip that switches it off, so nobody has to hold a legend
 * in their head. The counts come from `kind_counts`, which the report fills for
 * every kind including the ones at zero — so "no term changes this month" is
 * visible rather than being an absent chip.
 */
function KindFilter({
  state,
  setState,
  counts,
}: {
  state: CalendarPageState;
  setState: (next: CalendarPageState) => void;
  counts: Partial<Record<CalendarEventKind, number>> | undefined;
}): ReactNode {
  // An empty filter means every kind, so the chips all read as selected — which
  // is what the reader is actually looking at.
  const allSelected = state.kinds.length === 0;

  return (
    <div className={styles.kinds} role="group" aria-label="Kinds of event to show">
      {CalendarEventKind.options.map((kind) => {
        const selected = allSelected || state.kinds.includes(kind);
        const count = counts?.[kind];
        return (
          <button
            key={kind}
            type="button"
            aria-pressed={selected}
            className={`${styles.kindChip} ${styles[calendarEventKindTone(kind)]} ${
              selected ? styles.kindOn : ""
            }`}
            onClick={() => setState(toggleKind(state, kind))}
          >
            <span className={styles.kindDot} aria-hidden="true" />
            <span>{calendarEventKindLabel(kind)}</span>
            {count !== undefined && <span className={styles.kindCount}>{formatCount(count)}</span>}
          </button>
        );
      })}
      {!allSelected && (
        <Button small variant="subtle" onClick={() => setState({ ...state, kinds: [] })}>
          Show all
        </Button>
      )}
    </div>
  );
}

/**
 * Whose calendar this is. The scope control appears only once a partner is
 * chosen, because `rolled_up` and `direct` mean nothing without one — the report
 * scopes nothing, and offering the choice would be offering a control that
 * cannot change the answer.
 */
function PartnerFilter({
  state,
  setState,
}: {
  state: CalendarPageState;
  setState: (next: CalendarPageState) => void;
}): ReactNode {
  const partners = usePartnerOptions();

  return (
    <div className={styles.partnerFilter}>
      <label className={styles.filter}>
        <span className={styles.filterLabel}>Partner:</span>
        <select
          className={styles.select}
          value={state.partnerId ?? ""}
          onChange={(event) =>
            setState({ ...state, partnerId: event.target.value === "" ? null : event.target.value })
          }
        >
          <option value="">All partners</option>
          {(partners.data?.items ?? []).map((partner) => (
            <option key={partner.id} value={partner.id}>
              {partner.name}
            </option>
          ))}
        </select>
      </label>

      {state.partnerId !== null && (
        <Segmented
          label="Partner scope"
          variant="inset"
          options={
            [
              { value: "rolled_up", label: "+ owned", title: "This partner and every one it owns" },
              { value: "direct", label: "Direct", title: "This company alone" },
            ] as ReadonlyArray<SegmentedOption<ReportScope>>
          }
          value={state.scope}
          onChange={(scope) => setState({ ...state, scope })}
        />
      )}
    </div>
  );
}

function MonthGrid({
  model,
  monthLabel,
  currency,
  selected,
  onSelect,
}: {
  model: CalendarMonthModel;
  monthLabel: string;
  currency: Currency;
  selected: string | null;
  onSelect: (iso: string | null) => void;
}): ReactNode {
  const headings = weekdayHeadings();

  return (
    <div className={styles.gridWrap}>
      {/*
       * A real `<table>`, not the app's `Table` primitive: that one styles a
       * dense data table and a month grid is a different object. The semantics
       * are why it is a table at all — seven column headers a screen reader can
       * associate with each cell is what makes "the 15th, a Tuesday" recoverable
       * from a grid of loose divs.
       */}
      <table className={styles.grid}>
        <caption className={styles.caption}>{`What lands each day — ${monthLabel}`}</caption>
        <thead>
          <tr>
            {headings.map((heading) => (
              <th key={heading.long} scope="col" className={styles.weekday}>
                <span aria-hidden="true">{heading.short}</span>
                <span className="srOnly">{heading.long}</span>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {model.weeks.map((week) => (
            <tr key={week.key}>
              {week.days.map((day) => (
                <DayCell
                  key={day.iso}
                  day={day}
                  currency={currency}
                  isSelected={day.iso === selected}
                  onSelect={() => onSelect(day.iso === selected ? null : day.iso)}
                />
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function DayCell({
  day,
  currency,
  isSelected,
  onSelect,
}: {
  day: CalendarDay;
  currency: Currency;
  isSelected: boolean;
  onSelect: () => void;
}): ReactNode {
  const hidden = day.events.length - EVENTS_PER_DAY;
  const shown = day.events.slice(0, EVENTS_PER_DAY);
  const spoken = formatCalendarDate(day.iso);

  return (
    <td
      className={[
        styles.day,
        day.inMonth ? undefined : styles.outside,
        day.isToday && styles.today,
        isSelected && styles.selectedDay,
      ]
        .filter(Boolean)
        .join(" ")}
      // A real ARIA token, and the only mark on the cell that isn't colour:
      // "today" has to survive being read aloud and being printed.
      {...(day.isToday ? { "aria-current": "date" as const } : {})}
    >
      <div className={styles.dayHead}>
        <span className={`${styles.dayNumber} mono`} aria-hidden="true">
          {day.day}
        </span>
        {/* The number alone is ambiguous once it is out of its column. */}
        <span className="srOnly">{spoken}</span>
        {day.billCount > 0 && (
          <span
            className={`${styles.dayTotal} mono`}
            title={`${formatCount(day.billCount)} bill${day.billCount === 1 ? "" : "s"} due — gross. Cost recharges and suggested fees are not in this figure.`}
          >
            {formatMoney(day.billGrossMinor, currency, { omitCurrencyCode: true })}
          </span>
        )}
      </div>

      <ul className={styles.events}>
        {shown.map((event) => (
          <li key={event.id}>
            <EventChip event={event} currency={currency} />
          </li>
        ))}
      </ul>

      {day.events.length > 0 && (
        <button
          type="button"
          className={styles.more}
          aria-expanded={isSelected}
          aria-label={
            isSelected
              ? `Close the details for ${spoken}`
              : `Show all ${formatCount(day.events.length)} events on ${spoken}`
          }
          onClick={onSelect}
        >
          {isSelected ? "Close" : hidden > 0 ? `+${formatCount(hidden)} more` : "Open day"}
        </button>
      )}
    </td>
  );
}

/**
 * One event in a cell: a kind-coloured rule, who it is about, and the figure.
 *
 * The link is the headline, so the calendar is a way in to the record rather
 * than a dead end — a deal where the event has one, the partner otherwise, which
 * is where a term change and a partner-level recharge both belong.
 */
function EventChip({ event, currency }: { event: CalendarEvent; currency: Currency }): ReactNode {
  const text = describeCalendarEvent(event, currency);
  const target = calendarEventTarget(event);
  const label = calendarEventLabel(event, currency);

  return (
    <div className={`${styles.event} ${styles[calendarEventKindTone(event.kind)]}`}>
      {target.to === "deal" ? (
        <Link
          to="/deals/$dealId"
          params={{ dealId: target.id }}
          className={styles.eventLink}
          aria-label={label}
          title={label}
        >
          {text.headline}
        </Link>
      ) : (
        <Link
          to="/partners/$partnerId"
          params={{ partnerId: target.id }}
          className={styles.eventLink}
          aria-label={label}
          title={label}
        >
          {text.headline}
        </Link>
      )}
      {text.amount !== null && (
        <span className={`${styles.eventAmount} mono`} aria-hidden="true">
          {text.amount}
        </span>
      )}
    </div>
  );
}

/**
 * One day in full, under the grid.
 *
 * The cells cap at three events so the weeks stay the same height; this is where
 * the eleventh one is readable, with its summary line rather than only a name.
 */
function DayPanel({
  day,
  currency,
  onClose,
}: {
  day: CalendarDay;
  currency: Currency;
  onClose: () => void;
}): ReactNode {
  return (
    <Card>
      <CardHeader
        title={formatCalendarDate(day.iso)}
        description={`${formatCount(day.events.length)} event${day.events.length === 1 ? "" : "s"}${
          day.billCount === 0
            ? ""
            : ` · ${formatMoney(day.billGrossMinor, currency, { omitCurrencyCode: true })} of bills`
        }`}
        actions={
          <Button small onClick={onClose}>
            Close
          </Button>
        }
      />
      <EventList events={day.events} currency={currency} />
    </Card>
  );
}

function Agenda({
  days,
  currency,
}: {
  days: readonly {
    iso: string;
    isToday: boolean;
    isPast: boolean;
    events: readonly CalendarEvent[];
  }[];
  currency: Currency;
}): ReactNode {
  return (
    <div className={styles.agenda}>
      {days.map((day) => (
        <div
          key={day.iso}
          className={[styles.agendaDay, day.isPast && styles.agendaPast].filter(Boolean).join(" ")}
          {...(day.isToday ? { "aria-current": "date" as const } : {})}
        >
          <div className={styles.agendaDate}>
            <span className={styles.agendaDateText}>{formatCalendarDate(day.iso)}</span>
            {day.isToday && (
              <Badge tone="brand" shape="chip">
                Today
              </Badge>
            )}
          </div>
          <EventList events={day.events} currency={currency} />
        </div>
      ))}
    </div>
  );
}

/** The shared body of the day panel and the agenda — same rows, same order. */
function EventList({
  events,
  currency,
}: {
  events: readonly CalendarEvent[];
  currency: Currency;
}): ReactNode {
  return (
    <ul className={styles.eventRows}>
      {events.map((event) => {
        const text = describeCalendarEvent(event, currency);
        const target = calendarEventTarget(event);
        return (
          <li key={event.id} className={styles.eventRow}>
            <Badge tone={calendarEventKindTone(event.kind)} shape="chip">
              {calendarEventKindLabel(event.kind)}
            </Badge>
            <div className={styles.eventRowText}>
              {target.to === "deal" ? (
                <Link
                  to="/deals/$dealId"
                  params={{ dealId: target.id }}
                  className={styles.eventRowLink}
                >
                  {text.headline}
                </Link>
              ) : (
                <Link
                  to="/partners/$partnerId"
                  params={{ partnerId: target.id }}
                  className={styles.eventRowLink}
                >
                  {text.headline}
                </Link>
              )}
              <span className={styles.eventRowSummary}>{text.summary}</span>
            </div>
            <span className={`${styles.eventRowAmount} mono`}>{text.amount ?? ""}</span>
          </li>
        );
      })}
    </ul>
  );
}

function downloadCsv(
  events: readonly CalendarEvent[],
  currency: Currency,
  monthLabel: string,
): void {
  const csv = calendarCsv(events, currency);
  const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8" }));
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = `calendar-${monthLabel.toLowerCase().replace(/\s+/g, "-")}.csv`;
  anchor.click();
  URL.revokeObjectURL(url);
}
